import React, { useLayoutEffect } from "react";
import { ClaimText } from "../../components/ClaimText";
import {
  formatPatentDisplayCaption,
  getMatchScore,
  getScoreStyles,
} from "./accountDetailUtils";
import { partyTheme } from "./accountDetailTheme";

export const CLAIM_COMPARISON_BATCH_SIZE = 5;

export function claimComparisonCollapseKey(detectionId, claimDetectionId, index) {
  const id =
    claimDetectionId != null && claimDetectionId !== ""
      ? claimDetectionId
      : `idx-${index}`;
  return `${detectionId}:${id}`;
}

function AccountClaimsAnalysis({
  defaultPortfolio,
  selectedCompPortfolio,
  detections,
  defaultPortfolioPatents,
  competitorPortfolioPatents,
  selectedCompPortfolioId,
  expandedDetectionId,
  detectionDetails,
  patentClaimsByPatentUuid,
  onOpenClaimReference,
  onToggleDetection,
  scrollContainerRef,
  savedScrollTop,
  claimComparisonVisibleCount,
  setClaimComparisonVisibleCount,
  collapsedClaimComparisonKeys,
  onToggleCollapsedClaimComparisonKey,
}) {
  const expandedClaimCount =
    expandedDetectionId != null
      ? detectionDetails[expandedDetectionId]?.claim_detections?.length ?? 0
      : 0;

  useLayoutEffect(() => {
    const el = scrollContainerRef?.current;
    if (el == null) return;
    el.scrollTop = savedScrollTop ?? 0;
  }, [savedScrollTop, scrollContainerRef]);

  return (
    <main
      ref={scrollContainerRef}
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px",
        background: "#fff",
      }}
    >
      <div style={{ marginBottom: "16px" }}>
        <h1
          style={{
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: "6px",
            color: partyTheme.chrome.heading,
            lineHeight: 1.25,
            borderBottom: `1px solid ${partyTheme.chrome.titleRule}`,
            paddingBottom: "8px",
          }}
        >
          Claims Analysis:{" "}
          {defaultPortfolio?.portfolio_name || "My Portfolio"} vs{" "}
          {selectedCompPortfolio?.portfolio_name || "Competitor"}
        </h1>
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            color: "#5c5a54",
            fontSize: "12px",
            lineHeight: 1.3,
            marginTop: "6px",
          }}
        >
          <span>{detections.length} potential overlaps detected</span>
        </div>
      </div>

      {!selectedCompPortfolioId ? (
        <div
          style={{
            background: "#fff",
            border: `1px solid ${partyTheme.chrome.cardBorder}`,
            borderRadius: 0,
            padding: "28px",
            textAlign: "center",
            color: "#5c5a54",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <h3
            style={{
              fontSize: "15px",
              lineHeight: 1.25,
              marginBottom: "6px",
              color: partyTheme.chrome.heading,
              fontWeight: 600,
            }}
          >
            Select a Competitor Portfolio
          </h3>
          <p style={{ fontSize: "13px", lineHeight: 1.4 }}>
            Choose a competitor portfolio from the sidebar to see patent claim
            overlaps
          </p>
        </div>
      ) : detections.length === 0 ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid #d0d0d0",
            borderRadius: 0,
            padding: "28px",
            textAlign: "center",
            color: "#5c5a54",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <h3
            style={{
              fontSize: "15px",
              lineHeight: 1.25,
              marginBottom: "6px",
              color: partyTheme.chrome.heading,
              fontWeight: 600,
            }}
          >
            No Detections Yet
          </h3>
          <p style={{ fontSize: "13px", lineHeight: 1.4 }}>
            {!defaultPortfolioPatents.length || !competitorPortfolioPatents.length
              ? "Add patents to both portfolios to start detecting overlaps"
              : "No overlaps detected between these portfolios"}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {detections.map((detection) => {
            const isExpanded = expandedDetectionId === detection.detectionId;
            const details = detectionDetails[detection.detectionId];
            const myPatentClaims =
              patentClaimsByPatentUuid?.[detection.myPatentUuid] || null;
            const competitorPatentClaims =
              patentClaimsByPatentUuid?.[detection.compPatentUuid] || null;
            const claimCount = details?.claim_detections?.length ?? 0;
            const shownClaimCount = isExpanded
              ? Math.min(claimComparisonVisibleCount, claimCount)
              : 0;
            const visibleClaimDetections =
              isExpanded && details?.claim_detections?.length
                ? details.claim_detections.slice(0, shownClaimCount)
                : [];
            const remainingClaims = Math.max(0, claimCount - shownClaimCount);
            const myPortfolioPatent = defaultPortfolioPatents.find(
              (p) => p.patentUuid === detection.myPatentUuid,
            );
            const compPortfolioPatent = competitorPortfolioPatents.find(
              (p) => p.patentUuid === detection.compPatentUuid,
            );
            const myPatentDisplayName =
              myPortfolioPatent?.patent_name != null &&
              String(myPortfolioPatent.patent_name).trim() !== ""
                ? String(myPortfolioPatent.patent_name).trim()
                : null;
            const compPatentDisplayName =
              compPortfolioPatent?.patent_name != null &&
              String(compPortfolioPatent.patent_name).trim() !== ""
                ? String(compPortfolioPatent.patent_name).trim()
                : null;

            return (
              <div
                key={detection.id}
                style={{
                  background: "#fff",
                  border: `1px solid ${partyTheme.chrome.panelBorder}`,
                  borderRadius: 0,
                  overflow: "visible",
                  opacity: detection.dismissed ? 0.5 : 1,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  }}
                >
                <div
                  onClick={() => onToggleDetection(detection)}
                  style={{
                    padding: "10px 12px",
                    background: partyTheme.chrome.claimsRow,
                    borderBottom: isExpanded
                      ? `1px solid ${partyTheme.chrome.neutralRule}`
                      : "none",
                    display: "flex",
                    gap: "14px",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background =
                      partyTheme.chrome.claimsRowHover)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      partyTheme.chrome.claimsRow)
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                      lineHeight: 1.25,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: partyTheme.yours.text,
                        background: partyTheme.yours.bg,
                        border: `1px solid ${partyTheme.yours.border}`,
                        padding: "3px 8px",
                        borderRadius: 0,
                        wordBreak: "break-word",
                      }}
                      title={formatPatentDisplayCaption(
                        myPatentDisplayName,
                        detection.myPatentId,
                      )}
                    >
                      {formatPatentDisplayCaption(
                        myPatentDisplayName,
                        detection.myPatentId,
                      )}
                    </span>
                    <span style={{ color: "#6b6b69", fontWeight: 600 }}>×</span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: partyTheme.theirs.text,
                        background: partyTheme.theirs.bg,
                        border: `1px solid ${partyTheme.theirs.border}`,
                        padding: "3px 8px",
                        borderRadius: 0,
                        wordBreak: "break-word",
                      }}
                      title={formatPatentDisplayCaption(
                        compPatentDisplayName,
                        detection.compPatentId,
                      )}
                    >
                      {formatPatentDisplayCaption(
                        compPatentDisplayName,
                        detection.compPatentId,
                      )}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "12px",
                      lineHeight: 1.25,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        flexShrink: 0,
                      }}
                    >
                      <span>Similarity:</span>
                      <span
                        style={{
                          padding: "3px 8px",
                          borderRadius: "2px",
                          fontWeight: 600,
                          border: "1px solid rgba(0,0,0,0.08)",
                          ...getScoreStyles(detection.similarity),
                        }}
                      >
                        {detection.similarity}%
                      </span>
                    </div>
                    {detection.dismissed && (
                      <span
                        style={{
                          padding: "3px 8px",
                          borderRadius: "2px",
                          fontWeight: 600,
                          background: partyTheme.chrome.claimsSubhead,
                          color: "#5c5a54",
                          fontSize: "10px",
                          lineHeight: 1.2,
                          border: `1px solid ${partyTheme.chrome.neutralRule}`,
                        }}
                      >
                        Dismissed
                      </span>
                    )}
                    <span style={{ fontSize: "15px", color: "#999" }}>
                      {isExpanded ? "▼" : "▶"}
                    </span>
                  </div>
                </div>
                {isExpanded && details && claimCount > 0 && (
                  <div
                    style={{
                      padding: "6px 12px",
                      fontSize: "11px",
                      lineHeight: 1.35,
                      fontWeight: 500,
                      color: "#5c5a54",
                      borderBottom: `1px solid ${partyTheme.chrome.neutralRule}`,
                      background: "#fafafa",
                    }}
                  >
                    <strong
                      style={{
                        color: partyTheme.chrome.heading,
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      {claimCount}
                    </strong>{" "}
                    claim-level{" "}
                    {claimCount === 1 ? "detection" : "detections"} in this
                    overlap
                  </div>
                )}
                </div>

                {isExpanded && details && (
                  <div style={{ background: "#fff" }}>
                    {details.claim_detections &&
                    details.claim_detections.length > 0 ? (
                      visibleClaimDetections.map((claimDetection, index) => {
                        const collapseKey = claimComparisonCollapseKey(
                          detection.detectionId,
                          claimDetection.claim_detection_id,
                          index,
                        );
                        const isClaimCollapsed =
                          collapsedClaimComparisonKeys.has(collapseKey);

                        return (
                        <div
                          key={claimDetection.claim_detection_id}
                          style={{
                            borderBottom:
                              index < visibleClaimDetections.length - 1 ||
                              remainingClaims > 0
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                        >
                          <div
                            role="button"
                            tabIndex={0}
                            aria-expanded={!isClaimCollapsed}
                            aria-label={`Claim comparison ${
                              isClaimCollapsed ? "collapsed" : "expanded"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleCollapsedClaimComparisonKey(collapseKey);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.currentTarget.click();
                              }
                            }}
                            style={{
                              padding: "6px 10px",
                              background: partyTheme.chrome.claimsSubhead,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderBottom: `1px solid ${partyTheme.chrome.neutralRule}`,
                              cursor: "pointer",
                              boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                minWidth: 0,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "13px",
                                  color: "#999",
                                  lineHeight: 1,
                                  flexShrink: 0,
                                }}
                                aria-hidden
                              >
                                {isClaimCollapsed ? "▶" : "▼"}
                              </span>
                              <div
                                style={{
                                  fontSize: "11px",
                                  lineHeight: 1.3,
                                  fontWeight: 600,
                                  color: "#3d3d3a",
                                }}
                              >
                                Claim comparison {index + 1}
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontSize: "11px",
                                lineHeight: 1.25,
                                flexShrink: 0,
                                fontWeight: 500,
                                color: "#3d3d3a",
                              }}
                            >
                              <span>Match:</span>
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "2px",
                                  fontWeight: 600,
                                  border: "1px solid rgba(0,0,0,0.06)",
                                  ...getScoreStyles(
                                    getMatchScore(claimDetection.priority),
                                  ),
                                }}
                              >
                                {getMatchScore(claimDetection.priority)}%
                              </span>
                              {claimDetection.dismissed && (
                                <span
                                  style={{
                                    padding: "2px 8px",
                                    borderRadius: "2px",
                                    fontWeight: 600,
                                    fontSize: "10px",
                                    background: "#fff",
                                    color: "#5c5a54",
                                    border: `1px solid ${partyTheme.chrome.neutralRule}`,
                                  }}
                                >
                                  Dismissed
                                </span>
                              )}
                            </div>
                          </div>

                          {!isClaimCollapsed && (
                          <>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "1px",
                              background: "#e5e5e5",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                                padding: "5px 8px",
                                background: partyTheme.yours.bg,
                                borderBottom: `1px solid ${partyTheme.chrome.titleRule}`,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  fontWeight: 600,
                                  color: partyTheme.yours.textLabel,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.03em",
                                  lineHeight: 1.2,
                                }}
                              >
                                Your Patent
                              </span>
                              <span
                                style={{
                                  fontSize: "10px",
                                  lineHeight: 1.2,
                                  color: partyTheme.yours.text,
                                  background: "#fff",
                                  border: `1px solid ${partyTheme.yours.border}`,
                                  padding: "2px 6px",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              >
                                Claim {claimDetection.conflicting_claim_number}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                                padding: "5px 8px",
                                background: partyTheme.theirs.bg,
                                borderBottom: `1px solid ${partyTheme.theirs.border}`,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  fontWeight: 600,
                                  color: partyTheme.theirs.textLabel,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.03em",
                                  lineHeight: 1.2,
                                }}
                              >
                                Competitor Patent
                              </span>
                              <span
                                style={{
                                  fontSize: "10px",
                                  lineHeight: 1.2,
                                  color: partyTheme.theirs.text,
                                  background: "#fff",
                                  border: `1px solid ${partyTheme.theirs.border}`,
                                  padding: "2px 6px",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              >
                                Claim{" "}
                                {claimDetection.claim_number}
                              </span>
                            </div>
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "1px",
                              background: "#e5e5e5",
                            }}
                          >
                            <div
                              style={{
                                background: "#fff",
                                padding: "10px 12px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "14px",
                                  lineHeight: 1.55,
                                  color: "#222",
                                }}
                              >
                                <ClaimText
                                  text={claimDetection.conflicting_claim}
                                  claimType="yours"
                                  claimLookup={myPatentClaims?.claimsByNumber}
                                  patentLabel={formatPatentDisplayCaption(
                                    myPatentClaims?.patentDisplayName ??
                                      myPatentDisplayName,
                                    myPatentClaims?.patentDocumentNumber ??
                                      detection.myPatentId,
                                  )}
                                  onClaimReferenceClick={(claimNumber) =>
                                    onOpenClaimReference({
                                      portfolioId: detection.conflictingPortfolioId,
                                      patentUuid: detection.myPatentUuid,
                                      patentDocumentNumber: detection.myPatentId,
                                      claimNumber,
                                    })
                                  }
                                />
                              </div>
                            </div>
                            <div
                              style={{
                                background: "#fff",
                                padding: "10px 12px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "14px",
                                  lineHeight: 1.55,
                                  color: "#222",
                                }}
                              >
                                <ClaimText
                                  text={claimDetection.claim}
                                  claimType="competitor"
                                  claimLookup={
                                    competitorPatentClaims?.claimsByNumber
                                  }
                                  patentLabel={formatPatentDisplayCaption(
                                    competitorPatentClaims?.patentDisplayName ??
                                      compPatentDisplayName,
                                    competitorPatentClaims?.patentDocumentNumber ??
                                      detection.compPatentId,
                                  )}
                                  onClaimReferenceClick={(claimNumber) =>
                                    onOpenClaimReference({
                                      portfolioId: detection.portfolioId,
                                      patentUuid: detection.compPatentUuid,
                                      patentDocumentNumber: detection.compPatentId,
                                      claimNumber,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          </>
                          )}
                        </div>
                        );
                      })
                    ) : (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#999",
                        }}
                      >
                        No claim details available
                      </div>
                    )}
                    {remainingClaims > 0 && (
                      <div
                        style={{
                          padding: "10px 12px 12px",
                          borderTop: `1px solid ${partyTheme.chrome.neutralRule}`,
                          background: "#fafafa",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setClaimComparisonVisibleCount((c) =>
                              Math.min(
                                c + CLAIM_COMPARISON_BATCH_SIZE,
                                claimCount,
                              ),
                            );
                          }}
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            padding: "8px 16px",
                            border: `1px solid ${partyTheme.chrome.neutralRule}`,
                            borderRadius: 0,
                            background: "#fff",
                            color: "#1e3331",
                            cursor: "pointer",
                            lineHeight: 1.3,
                          }}
                        >
                          Show more
                          <span
                            style={{
                              fontWeight: 500,
                              color: "#6b6b69",
                              marginLeft: "6px",
                            }}
                          >
                            ({remainingClaims} more)
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {isExpanded && !details && (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    Loading claim comparisons...
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}

export default AccountClaimsAnalysis;
